import { useState, FormEvent } from 'react'
import FormField from '../../../components/common/FormField'
import FileUpload from '../../../components/common/FileUpload'
import Button from '../../../components/common/Button'
import { getImageUrl } from '../../../utils/helpers'
import { TeamMember, TeamFormData } from '../Team'

interface TeamFormProps {
  initialData?: TeamMember
  onSubmit: (data: TeamFormData) => Promise<void>
  onCancel: () => void
}

function TeamForm({ initialData, onSubmit, onCancel }: TeamFormProps) {
  const [formData, setFormData] = useState<TeamFormData>({
    name: initialData?.name || '',
    role: initialData?.role || '',
    bio: initialData?.bio || '',
    media_id: initialData?.media_id,
    position: initialData?.position || 0,
    featured: initialData?.featured || false,
    is_active: initialData?.is_active ?? true,
  })

  const [photoPreview, setPhotoPreview] = useState<string>(
    initialData?.photo?.url ? getImageUrl(initialData.photo.url) : ''
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }))

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleFileUpload = (fileData: any) => {
    if (fileData && fileData.id) {
      setFormData((prev) => ({
        ...prev,
        media_id: fileData.id,
      }))
      // Set preview URL for display
      if (fileData.url || fileData.file_path) {
        const imagePath = fileData.url || fileData.file_path
        setPhotoPreview(getImageUrl(imagePath))
      }
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis'
    }

    if (!formData.role.trim()) {
      newErrors.role = 'Le rôle est requis'
    }

    if (!formData.bio.trim()) {
      newErrors.bio = 'La biographie est requise'
    }

    if (formData.position < 0) {
      newErrors.position = 'La position doit être positive'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    try {
      setSubmitting(true)
      setError('')
      await onSubmit(formData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="team-form">
      {error && <div className="form-error">{error}</div>}

      <FormField
        id="name"
        name="name"
        label="Nom complet"
        value={formData.name}
        onChange={handleChange}
        error={errors.name}
        required
      />

      <FormField
        id="role"
        name="role"
        label="Rôle / Fonction"
        value={formData.role}
        onChange={handleChange}
        error={errors.role}
        required
      />

      <FormField
        id="bio"
        name="bio"
        label="Biographie"
        value={formData.bio}
        onChange={handleChange}
        error={errors.bio}
        textarea
        required
      />

      <div className="form-field">
        <label>Photo</label>
        <FileUpload
          onUpload={handleFileUpload}
          accept="image/*"
          label="Choisir une photo"
        />
        {photoPreview && (
          <div className="photo-preview">
            <img src={photoPreview} alt="Preview" />
          </div>
        )}
      </div>

      <FormField
        id="position"
        name="position"
        label="Position (ordre d'affichage)"
        type="number"
        value={formData.position}
        onChange={handleChange}
        error={errors.position}
        min="0"
        required
      />

      <div className="form-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="featured"
            checked={formData.featured}
            onChange={handleChange}
          />
          <span>Membre en vedette</span>
        </label>
      </div>

      <div className="form-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          <span>Membre actif</span>
        </label>
      </div>

      <div className="form-actions">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </form>
  )
}

export default TeamForm
